import { Container, Row, Col } from 'reactstrap';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import DisplayQueryError from './DisplayQueryError';
import LoadingSpinner from './LoadingSpinner';
import useGetRelatedQuestionsAndArticles from '../../api/useGetRelatedQuestionsAndArticles';
import { createUrlFromQuestion } from '../knowledge/createUrlFromQuestion';
import { Tag } from '../../types';

interface RelatedQuestionsAndArticlesProps {
    qnaEntityId: number;
    tags: Tag[];
}

const RelatedQuestionsAndArticles: React.FC<
    RelatedQuestionsAndArticlesProps
> = ({ qnaEntityId, tags }) => {
    const getRelatedQuestionsAndArticles = useGetRelatedQuestionsAndArticles({
        qnaEntityId,
        tags: tags.map((tag) => tag.text)
    });

    const tagsKey = tags.map((tag) => tag.text).join();

    const {
        data: relatedQuestionsAndArticles,
        isLoading,
        error,
        refetch
    } = useQuery(
        ['relatedQuestionsAndArticles', qnaEntityId, tagsKey],
        getRelatedQuestionsAndArticles
    );

    if (error) {
        return <DisplayQueryError error={error} refetch={refetch} />;
    }

    if (isLoading || !relatedQuestionsAndArticles) {
        return <LoadingSpinner />;
    }

    const { questions, articles } = relatedQuestionsAndArticles;

    if (questions.length === 0 && articles.length === 0) {
        return null;
    }

    return (
        <Container className='py-3'>
            <div className='custom-card my-4'>
                <div className='custom-card-head'>
                    <div className='custom-card-heading d-flex align-items-center'>
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            viewBox='0 0 512 512'
                            className='custom-card-icon'
                        >
                            <defs>
                                <linearGradient
                                    id='linear-gradient'
                                    y1='-0.14'
                                    x2='1.037'
                                    y2='1.226'
                                    gradientUnits='objectBoundingBox'
                                >
                                    <stop
                                        offset='0'
                                        stopColor='var(--primary)'
                                    />
                                    <stop
                                        offset='1'
                                        stopColor='var(--primary-light)'
                                    />
                                </linearGradient>
                            </defs>
                            <path
                                fill='url(#linear-gradient)'
                                d='M43.246 466.142c-58.43-60.289-57.341-157.511 1.386-217.581L254.392 34c44.316-45.332 116.351-45.336 160.671 0 43.89 44.894 43.943 117.329 0 162.276L232.214 383.128c-29.855 30.537-78.633 30.111-107.982-.998-28.275-29.97-27.368-77.473 1.452-106.953l143.743-146.835c6.182-6.314 16.312-6.422 22.626-.241l22.861 22.379c6.315 6.182 6.422 16.312.241 22.626L171.427 319.927c-4.932 5.045-5.236 13.428-.648 18.292 4.372 4.634 11.245 4.711 15.688.165l182.849-186.851c19.613-20.062 19.613-52.725-.011-72.798-19.189-19.627-49.957-19.637-69.154 0L90.39 293.295c-34.763 35.56-35.299 93.12-1.191 128.313 34.01 35.093 88.985 35.137 123.058.286l172.06-175.999c6.177-6.319 16.307-6.433 22.626-.256l22.877 22.364c6.319 6.177 6.434 16.307.256 22.626l-172.06 175.998c-59.576 60.938-155.943 60.216-214.77-.485z'
                            />
                        </svg>
                        <h3 className='mb-0 ms-2'>Related</h3>
                    </div>
                </div>
                <div className='custom-card-body mt-3'>
                    <Row>
                        {questions.length > 0 && (
                            <Col xs='12' md='6' className='border-end'>
                                <h4>Questions</h4>
                                {questions.map((question) => (
                                    <div key={question.questionId}>
                                        <Link
                                            to={createUrlFromQuestion(
                                                question.questionId,
                                                question.text
                                            )}
                                            className='question-link'
                                        >
                                            {question.text}
                                        </Link>
                                    </div>
                                ))}
                            </Col>
                        )}
                        {articles.length > 0 && (
                            <Col xs='12' md='6'>
                                <h4>Articles</h4>
                                {articles.map((article) => (
                                    <div key={article.id}>
                                        <Link
                                            to={`/article/${article.slug}`}
                                            className='question-link'
                                        >
                                            {article.title}
                                        </Link>
                                    </div>
                                ))}
                            </Col>
                        )}
                    </Row>
                </div>
            </div>
        </Container>
    );
};

export default RelatedQuestionsAndArticles;
